import type { Chat } from "@storytell/cp-client";
import { createStore } from "solid-js/store";
import {
  mergeOlderRecordings,
  processChatRecording,
  processStreamingChat,
  startStreamingChat,
} from "./handlers/streamingChatHandlers";
import { cleanupChat, cleanupMessage, evictChatsExcept, getChatStatus, removeMessageFromChat } from "./streamingChat";
import type {
  StreamingBlockId,
  StreamingChatId,
  StreamingMessageId,
  StreamingOperationId,
  StreamingStore,
} from "./streamingTypes";

export const createStreamingStore = () => {
  const [state, setState] = createStore<StreamingStore>({
    // A chat is a collection of messages
    chats: {},
    // A message is a group of a prompt and a response made up
    // of a series of operations
    messages: {},
    // An operation is a single step in a message, made up of a series of blocks
    operations: {},
    // Blocks are the smallest unit of content in a message
    blocks: {},
    // Operations that arrived before their parent message
    orphanedOperations: {},
    // Deltas that arrived before their block
    bufferedDeltas: {},
  });

  // TODO: @andi Split these out into separate files?
  const methods = {
    chat: {
      startStreamingChat: (opts: {
        request: Chat.StreamRequest;
        stream: AsyncIterable<Chat.StreamResponse>;
        onChatIdCaptured?: (chatId: StreamingChatId) => void;
        onMessageStarted?: (messageId: StreamingMessageId) => void;
        onMessageFinished?: (messageId: StreamingMessageId) => void;
        onMessageFailed?: (messageId: StreamingMessageId) => void;
        onStreamError?: (error: unknown) => void;
        onStreamInterrupted?: () => void;
        abortSignal?: AbortSignal;
      }) => startStreamingChat({ store: state, setStore: setState, ...opts }),
      /**
       * Process an async stream of chat messages
       * Use it for live streaming chats
       * @param stream - The async iterable stream of chat messages
       * @param abortSignal - Optional signal to abort stream processing
       * @returns "completed" once the turn ended, "interrupted" when the stream stopped short of that
       */
      processStreamingChat: (
        stream: AsyncIterable<Chat.StreamResponse> | AsyncIterable<Chat.StreamPlaybackResponse>,
        abortSignal?: AbortSignal
      ) => processStreamingChat(state, setState, stream, undefined, undefined, undefined, undefined, abortSignal),
      /**
       * Process a chat recording
       * Use it for saved chats, will perform synchronous processing
       * @param recording - The chat recording
       * @param keep - Chats the caller still has mounted; everything else is dropped
       * @returns void
       */
      processChatRecording: (
        recording: Chat.ConversationResponse,
        keep: ReadonlySet<StreamingChatId>,
        activeMessageId?: string
      ) => {
        processChatRecording(state, setState, recording, activeMessageId);
        evictChatsExcept(setState, keep);
      },
      /**
       * Merge older paginated recordings into a loaded chat without clearing existing content.
       * Returns the number of messages newly added.
       */
      mergeOlderRecordings: (recording: Chat.ConversationResponse) => mergeOlderRecordings(state, setState, recording),

      get: (chatId: StreamingChatId) => state.chats[chatId],
      getStatus: (chatId: StreamingChatId) => getChatStatus(state, chatId),

      cleanup: (chatId: StreamingChatId) => cleanupChat(setState, chatId),
      evictExcept: (keep: ReadonlySet<StreamingChatId>, opts?: { includeGenerating?: boolean }) =>
        evictChatsExcept(setState, keep, opts),
      removeMessage: (chatId: StreamingChatId, messageId: StreamingMessageId) =>
        removeMessageFromChat(setState, chatId, messageId),
    },

    message: {
      get: (messageId: StreamingMessageId) => state.messages[messageId],
      cleanup: (messageId: StreamingMessageId) => cleanupMessage(setState, messageId),
    },

    operation: {
      get: (operationId: StreamingOperationId) => state.operations[operationId],
    },

    block: {
      get: (blockId: StreamingBlockId) => state.blocks[blockId],
    },
  };

  return {
    state,
    hydrate: setState,
    ...methods,
  } as const;
};

export type StreamingService = ReturnType<typeof createStreamingStore>;
